import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';

import { DeleteModal } from '../../../../common/deleteModal';
import { CreditsPoolsDashboardWithData } from '.';
import { CreditsPoolsDashboardContext, OverviewContextType } from '../context';
import { getCreditsPoolsSelector } from '../../../../../slices/selectors/creditPool';
import { IIdentifier } from '../../../../../slices/accounts/account';
import { RootState } from '../../../../../slices/rootReducer';
import { ICreditsPoolOverview } from '../CreditsPoolOverview';
import { TypeDispatchFunction } from '../../../../../tsHelpers';
import { deleteCreditPool, toggleArchiveCreditPool } from '../../../../../slices/credits/creditsPools/creditsPools.thunk';
import { fetchHubspotAccountById } from '../../../../../slices/accounts/hubspotAccounts.thunks';
import { Features, isFeatureEnabled } from '../../../../../featureToggles/features';
import { isNetquestClient } from '../../../../../utils/clients';

interface State {
  isDeleteCreditsPoolModalOpen: boolean;
  selectedCreditsPoolToBeDeleted: {
    id: string;
    name: string;
  } | null;
}

interface IMapStateToProps {
  creditsPools: ICreditsPoolOverview[];
  accountId?: string;
}

interface IMapDispatchToProps {
  archiveCreditPool: TypeDispatchFunction<typeof toggleArchiveCreditPool>;
  deleteCreditPool: TypeDispatchFunction<typeof deleteCreditPool>;
  fetchHubspotAccountById: TypeDispatchFunction<typeof fetchHubspotAccountById>;
}

type Props = IMapStateToProps & IMapDispatchToProps;

class CreditsPoolsDashboardWithDataContainerBase extends React.Component<Props, State> {
  private memoizedContextValue: OverviewContextType;
  constructor(props: Props) {
    super(props);

    this.state = {
      isDeleteCreditsPoolModalOpen: false,
      selectedCreditsPoolToBeDeleted: null
    };

    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.openDeleteCreditPoolModal = this.openDeleteCreditPoolModal.bind(this);
    this.archiveCreditPool = this.archiveCreditPool.bind(this);
    this.onDelete = this.onDelete.bind(this);

    this.memoizedContextValue = {
      onDeleteCreditsPoolClick: this.openDeleteCreditPoolModal,
      onArchiveCreditsPoolClick: this.archiveCreditPool
    };
  }

  componentDidMount() {
    const { accountId, fetchHubspotAccountById } = this.props;
    if (isFeatureEnabled(Features.CreditPoolExtension) && isNetquestClient() && accountId) {
      fetchHubspotAccountById(accountId);
    }
  }

  private openModal() {
    this.setState({ isDeleteCreditsPoolModalOpen: true });
  }

  private closeModal() {
    this.setState({ isDeleteCreditsPoolModalOpen: false });
  }

  private onDelete() {
    const { selectedCreditsPoolToBeDeleted } = this.state;
    if (selectedCreditsPoolToBeDeleted) {
      this.props.deleteCreditPool(selectedCreditsPoolToBeDeleted.id);
    }
    this.closeModal();
  }

  private openDeleteCreditPoolModal(creditsPoolId: string, name: string ) {
    this.setState(
      {
        selectedCreditsPoolToBeDeleted: {
          name,
          id: creditsPoolId
        }
      },
      () => {
        this.openModal();
      }
    );
  }

  private archiveCreditPool(creditPoolId: string, name: string ) {
    this.props.archiveCreditPool({ creditPoolId });
  }

  private renderDeleteCreditsPoolModal() {
    const { isDeleteCreditsPoolModalOpen, selectedCreditsPoolToBeDeleted } = this.state;
    if (!isDeleteCreditsPoolModalOpen) {
      return null;
    }

    return (
      <DeleteModal
        title='Delete credit pool'
        description={`You are about to delete the credit pool ${selectedCreditsPoolToBeDeleted?.name.trim()}, are you sure you want to proceed?`}
        highlightOnDescriptionText={selectedCreditsPoolToBeDeleted?.name.trim()}
        subdescription='This action cannot be undone.'
        onCancel={this.closeModal}
        onAccept={this.onDelete}
        onClose={this.closeModal}
      />
    );
  }

  render() {
    const { creditsPools } = this.props;
    return (
      <CreditsPoolsDashboardContext.Provider value={this.memoizedContextValue} >
        <CreditsPoolsDashboardWithData creditsPools={creditsPools} />
        {this.renderDeleteCreditsPoolModal()}
      </CreditsPoolsDashboardContext.Provider>
    );
  }
}

const mapStateToProps = (state: RootState, props: RouteComponentProps<IIdentifier>): IMapStateToProps => {
  const { id: accountId = '' } = props.match.params;
  const selectCreditsPools = getCreditsPoolsSelector(state);
  const creditsPools = selectCreditsPools(state);

  return {
    creditsPools,
    accountId
  };
};

const mapDispatchToProps: IMapDispatchToProps = {
  archiveCreditPool: toggleArchiveCreditPool,
  deleteCreditPool,
  fetchHubspotAccountById
};

const CreditsPoolsDashboardWithDataContainer = withRouter(connect(mapStateToProps, mapDispatchToProps)(CreditsPoolsDashboardWithDataContainerBase));

export { CreditsPoolsDashboardWithDataContainer };
